import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { 
  User, Mail, Phone, Building, Briefcase, Clock, CheckCircle, 
  XCircle, Send, AlertTriangle, Loader2, RefreshCw, Calendar,
  MessageSquare, MessageCircle, FileText, Plus, Edit, Shield, Eye, Download
} from 'lucide-react';
import { formatBackendDate, formatBackendDateTime } from '../../lib/dateUtils';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

const SLOT_REASON_OPTIONS = [
  { value: 'earlier_employment', label: 'Earlier employer (not most recent)' },
  { value: 'agency_placement', label: 'Agency placement' },
  { value: 'name_change', label: 'Employer name changed / different spelling' },
  { value: 'recent_employer_not_required', label: 'Recent employer reference not required' },
  { value: 'referee_work_email', label: 'Referee will reply from work email' },
  { value: 'employer_name_variation', label: 'Employer name variation' },
  { value: 'other', label: 'Other (explain below)' },
];

const REFERENCE_RESEND_STATUSES = new Set([
  'sent',
  'requested',
  'request_sent',
  'awaiting_response',
  'submitted',
  'awaiting_review',
]);

export function referenceRequestIsResend(reference) {
  const request = reference?.request || {};
  const channels = request.channels_delivered;
  if (Array.isArray(channels)) {
    // Empty is meaningful: a modern request may have been prepared or tried,
    // but neither email nor Meta WhatsApp accepted it.
    return channels.some((channel) => ['email', 'whatsapp'].includes(String(channel || '').toLowerCase()));
  }
  if (request.last_requested_channel || request.delivery) {
    // Modern channel-aware request with no delivery evidence: not a resend.
    return false;
  }
  const status = String(reference?.status || '').toLowerCase();
  const requestStatus = String(request.status || '').toLowerCase();
  return Boolean(
    request.sent_at
    || REFERENCE_RESEND_STATUSES.has(status)
    || REFERENCE_RESEND_STATUSES.has(requestStatus)
  );
}

export function referenceDeliveredChannels(reference) {
  const request = reference?.request || {};
  const channels = request.channels_delivered;
  if (Array.isArray(channels)) {
    return channels
      .map((channel) => String(channel || '').trim().toLowerCase())
      .filter((channel) => channel === 'email' || channel === 'whatsapp');
  }

  // Infer legacy email delivery only for records created before per-channel
  // tracking. A failed modern WhatsApp send must never appear email-delivered.
  if (!request.last_requested_channel && !request.delivery && referenceRequestIsResend(reference)) {
    return ['email'];
  }
  return [];
}

export function referenceRequestShouldForceResend(reference, channel) {
  if (!referenceRequestIsResend(reference)) return false;
  const delivered = new Set(referenceDeliveredChannels(reference));
  const requested = channel === 'both' ? ['email', 'whatsapp'] : [channel];
  return requested.every((item) => delivered.has(item));
}

export function referenceIsFinalised(reference) {
  const status = String(reference?.status || '').toLowerCase();
  const verificationStatus = String(reference?.verification?.status || '').toLowerCase();
  const verified = Boolean(
    reference?.verified === true
    || reference?.verification?.verified === true
    || status === 'verified'
    || verificationStatus === 'verified'
  );
  const unresolvedMismatch = Boolean(
    reference?.counts_toward_readiness === false
    || (reference?.mismatch?.detected && !reference?.mismatch?.resolved)
    || reference?.awaiting_worker_explanation
  );
  return verified && !unresolvedMismatch;
}

const STATUS_CONFIG = {
  not_declared: { label: 'Missing', color: 'bg-gray-100 text-gray-600', icon: XCircle },
  declared: { label: 'Referee declared', color: 'bg-blue-100 text-blue-700', icon: Clock },
  sent: { label: 'Awaiting response', color: 'bg-amber-100 text-amber-700', icon: Send },
  response_received: { label: 'Response received — awaiting admin review', color: 'bg-purple-100 text-purple-700', icon: MessageSquare },
  verified: { label: 'Satisfactory', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Unsatisfactory — worker action required', color: 'bg-red-100 text-red-700', icon: XCircle },
  legacy_unverified: { label: 'Declared referee on file — response evidence not found', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  mismatch: { label: 'Mismatch — explanation required', color: 'bg-amber-100 text-amber-800', icon: AlertTriangle },
};

/**
 * Derive the display status for a reference, accounting for legacy data.
 * A reference is only "satisfactory" if the backend says counts_toward_readiness=true.
 * This ensures admin + worker see the same truth and that an unresolved mismatch
 * (e.g. not from the most recent employer) never renders as green "Satisfactory".
 * Legacy employee-level "verified" flags without canonical evidence are downgraded.
 */
export function deriveDisplayStatus(ref) {
  const status = ref?.status || 'not_declared';
  const response = ref?.response;
  const verification = ref?.verification || {};
  const mismatch = ref?.mismatch || {};
  const hasCanonicalResponse = Boolean(
    response && typeof response === 'object' && Object.keys(response).length > 0
  );
  const hasAdminStamp = Boolean(verification.verified_by || verification.verified_at);

  if (status === 'verified') {
    // Backend is the source of truth for readiness. If it says this reference
    // does NOT count toward readiness (unresolved mismatch), never show green.
    if (ref && ref.counts_toward_readiness === false) {
      return 'mismatch';
    }
    if (mismatch.detected && !mismatch.resolved) {
      return 'mismatch';
    }
    if (!hasCanonicalResponse && !hasAdminStamp) {
      return 'legacy_unverified';
    }
    if (!hasCanonicalResponse) {
      return 'legacy_unverified';
    }
    // A genuine verified response must stay satisfactory. Only an unresolved
    // mismatch can downgrade it; the arrival of a response never overrides
    // the authorised final manager decision.
    return 'verified';
  }
  // Canonical request/response evidence outranks a stale flat-field badge.
  // A prepared WhatsApp message is not delivery confirmation. Conversely a
  // referee response is not a manager's satisfactory decision.
  if (hasCanonicalResponse && status !== 'rejected') {
    return 'response_received';
  }
  const delivered = referenceDeliveredChannels(ref);
  if (delivered.length > 0 && !['verified', 'rejected'].includes(status)) {
    return 'sent';
  }
  if (['sent', 'requested', 'request_sent', 'awaiting_response'].includes(status)
    && Array.isArray(ref?.request?.channels_delivered)
    && ref.request.channels_delivered.length === 0) {
    return 'declared';
  }
  return status;
}

function getMismatchTypeLabel(typeValue) {
  const t = String(typeValue || '').toLowerCase();
  if (t.includes('identity') || t.includes('email') || t.includes('name') || t.includes('organisation')) {
    return 'referee details mismatch';
  }
  if (t.includes('employment') || t.includes('recent_employer') || t.includes('earlier_employer')) {
    return 'employment mismatch';
  }
  return 'mismatch';
}

export function referenceWhatsAppTemplateCopy(state) {
  if (!state) return 'CareOps has not checked Meta yet.';
  if (state.ready === true) return 'Meta has approved the referee template. WhatsApp first contact is available when an admin chooses WhatsApp.';
  const status = String(state.status || '').toUpperCase();
  if (status === 'PENDING' || status === 'IN_APPEAL') {
    return 'Meta is still reviewing the referee WhatsApp template. Use the prepared manual WhatsApp message until approval is complete.';
  }
  if (status === 'REJECTED') {
    return 'Meta rejected the referee WhatsApp template. Template-based first contact stays unavailable until the template is corrected and approved.';
  }
  if (status === 'PAUSED' || status === 'DISABLED') {
    return `Meta has marked the referee WhatsApp template ${status.toLowerCase()}. Template-based first contact is unavailable.`;
  }
  if (state.found === false && state.checked === true) {
    return 'Meta cannot find the referee request template for this language. Automatic WhatsApp requests are unavailable until Meta approves it. You can prepare a manual WhatsApp request below.';
  }
  if (state.reason === 'template_wrong_category') {
    return 'Meta has the referee template, but it is not approved as a UTILITY template, so CareOps will not use it automatically.';
  }
  return 'CareOps cannot confirm an approved Meta referee template yet. Use the prepared manual WhatsApp message for now.';
}

export default function ReferencesPanel({ employeeId, employee, onRefresh, onEditReference }) {
  const [references, setReferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [workflowError, setWorkflowError] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(null);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [selectedRef, setSelectedRef] = useState(null);
  const [customMessage, setCustomMessage] = useState('');
  const [deliveryChannel, setDeliveryChannel] = useState('email');
  const [manualWhatsAppUrl, setManualWhatsAppUrl] = useState(null);
  const [whatsappTemplateStatus, setWhatsAppTemplateStatus] = useState(null);
  const [checkingWhatsAppTemplate, setCheckingWhatsAppTemplate] = useState(false);
  
  // Add referee dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addRefNum, setAddRefNum] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [reasonRefNum, setReasonRefNum] = useState(null);
  const [reasonType, setReasonType] = useState('');
  const [reasonNote, setReasonNote] = useState('');
  const [savingReason, setSavingReason] = useState(false);
  const [refereeForm, setRefereeForm] = useState({
    name: '',
    email: '',
    phone: '',
    organisation: '',
    position: '',
    relationship: '',
    slot_reason_type: '',
    slot_reason_note: '',
  });
  
  // Review Response modal state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewRefNum, setReviewRefNum] = useState(null);
  
  // Verify/Reject state
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [verifyRefNum, setVerifyRefNum] = useState(null);
  const [verifyAction, setVerifyAction] = useState('verify'); // 'verify' or 'reject'
  const [verifyNotes, setVerifyNotes] = useState('');
  const [mismatchReason, setMismatchReason] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  // Reg 19 sufficiency inputs
  const [referenceType, setReferenceType] = useState('employment');
  const [explanationReason, setExplanationReason] = useState('');
  const [explanationRequired, setExplanationRequired] = useState(false);

  // Recent-employer mismatch flag state
  const [flaggingMismatch, setFlaggingMismatch] = useState(null); // ref_num being flagged

  // Mismatch explanation review state (admin reviews worker explanation)
  const [reviewExplanationOpen, setReviewExplanationOpen] = useState(false);
  const [reviewExplanationRef, setReviewExplanationRef] = useState(null);
  const [reviewDecision, setReviewDecision] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewingExplanation, setReviewingExplanation] = useState(false);

  // PDF download state
  const [downloadingPdf, setDownloadingPdf] = useState(null);

  const fetchReferences = async () => {
    try {
      setLoading(true);
      setLoadError(false);
      setWorkflowError(false);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees/${employeeId}/references`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Dedicated manager preflight calculates the SAME tenant-scoped
      // contact and employer-review decisions used for actual outreach.
      // Older flat reference summaries can otherwise show 'cannot send'
      // while the employment comparison correctly recognises the employer.
      let merged = response.data;
      try {
        const workflow = await axios.get(
          `${API}/reference-outreach/employees/${employeeId}/workflow`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const computed = workflow.data?.references || {};
        merged = {
          ...response.data,
          references: Object.fromEntries([1, 2].map(n => {
            const key = `reference_${n}`;
            const existing = response.data?.references?.[key] || {};
            const current = computed[key] || {};
            return [key, { ...existing, ...current,
              workflow_stage: current.stage || null,
              workflow_next_action: current.next_action || null,
            }];
          })),
        };
      } catch (preflightError) {
        setWorkflowError(true);
        console.warn('Referee workflow preflight unavailable', preflightError);
        // The send endpoint still checks consent, ownership and eligibility.
        // Do not destroy the saved referee records because a read failed.
      }
      setReferences(merged);
    } catch (error) {
      console.error('Failed to fetch references:', error);
      setReferences(null);
      setLoadError(true);
      toast.error('Failed to load references');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employeeId) {
      fetchReferences();
    }
  }, [employeeId]);

  const handleSendRequest = async (refNum, channel = deliveryChannel) => {
    try {
      setSendingRequest(refNum);
      setManualWhatsAppUrl(null);
      const token = localStorage.getItem('token');
      const currentRef = references?.references?.[`reference_${refNum}`] || {};
      const declared = currentRef?.declared || {};

      if (referenceIsFinalised(currentRef)) {
        toast.info('This reference is already satisfactory. Use the audited reset/change flow if it needs to be replaced.');
        return;
      }
      const responseAlreadyReceived = Boolean(
        currentRef?.response
        && typeof currentRef.response === 'object'
        && Object.keys(currentRef.response).length > 0
      );
      if (responseAlreadyReceived) {
        toast.info('The referee has already replied. Review the response instead of sending another request.');
        return;
      }

      if ((channel === 'email' || channel === 'both') && !declared.email) {
        toast.error('Add the referee email address before sending by email.');
        return;
      }
      if ((channel === 'whatsapp' || channel === 'both') && !declared.phone) {
        toast.error('Add the referee phone number before sending by WhatsApp.');
        return;
      }

      const forceResend = referenceRequestShouldForceResend(currentRef, channel);
      const params = {
        reference_num: refNum,
        force_resend: forceResend,
        channel,
      };
      if (customMessage.trim()) params.message = customMessage.trim();

      const response = await axios.post(
        `${API}/employees/${employeeId}/send-reference-request`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
        }
      );

      const result = response.data || {};
      if (
        result.whatsapp_template_checked !== undefined
        || result.whatsapp_template_status
        || result.whatsapp_template_found !== undefined
      ) {
        setWhatsAppTemplateStatus((previous) => ({
          ...(previous || {}),
          checked: result.whatsapp_template_checked === true,
          found: result.whatsapp_template_found === true,
          approved: result.whatsapp_template_status === 'APPROVED',
          ready: (
            result.whatsapp_template_status === 'APPROVED'
            && result.whatsapp_template_category === 'UTILITY'
          ),
          status: result.whatsapp_template_status || null,
          category: result.whatsapp_template_category || null,
          language: result.whatsapp_template_language || null,
          reason: result.whatsapp_error || null,
        }));
      }
      if (result.status === 'duplicate') {
        toast.info(result.message || 'That request is already awaiting a response through the selected channel.');
      } else if (result.status === 'email_failed') {
        toast.warning(result.message || 'Request saved but email failed to send. Check email configuration and try again.');
      } else if (result.status === 'success') {
        toast.success(result.message || `Reference request sent to referee ${refNum}`);
        setSendDialogOpen(false);
        setCustomMessage('');
      } else if (result.status === 'partial_success') {
        toast.warning(result.message || 'One delivery channel succeeded and another needs attention.');
        if (result.manual_whatsapp_url) {
          setManualWhatsAppUrl(result.manual_whatsapp_url);
        }
      } else if (result.status === 'whatsapp_manual_required') {
        setManualWhatsAppUrl(result.manual_whatsapp_url || null);
        toast.warning(result.message || 'Open WhatsApp to finish sending this request.');
      } else if (result.status === 'delivery_failed') {
        toast.error(result.message || 'No selected delivery channel succeeded.');
      } else {
        toast.error(result.message || 'Failed to send reference request');
      }

      await fetchReferences();
      if (onRefresh) await onRefresh();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to send request'));
    } finally {
      setSendingRequest(null);
    }
  };

  const fetchReferenceWhatsAppTemplateStatus = async () => {
    setCheckingWhatsAppTemplate(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/reference-outreach/whatsapp/template-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWhatsAppTemplateStatus(response.data || null);
      return response.data || null;
    } catch (error) {
      console.warn('Could not load referee WhatsApp template status', error);
      const fallback = {
        checked: false,
        found: false,
        approved: false,
        ready: false,
        status: null,
        reason: 'status_unavailable',
      };
      setWhatsAppTemplateStatus(fallback);
      return fallback;
    } finally {
      setCheckingWhatsAppTemplate(false);
    }
  };

  const openSendDialog = (refNum) => {
    setSelectedRef(refNum);
    setCustomMessage('');
    setDeliveryChannel('email');
    setManualWhatsAppUrl(null);
    setWhatsAppTemplateStatus(null);
    setSendDialogOpen(true);
    fetchReferenceWhatsAppTemplateStatus();
  };
  
  // Open review response modal
  const openReviewDialog = (refNum) => {
    setReviewRefNum(refNum);
    setReviewDialogOpen(true);
  };

  // Download reference response as PDF
  const handleDownloadReferencePdf = async (refNum) => {
    try {
      setDownloadingPdf(refNum);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/references/${employeeId}/${refNum}/download-pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const empName = `${employee?.first_name || ''}_${employee?.last_name || ''}`.replace(/\s+/g, '_');
      link.download = `reference_${refNum}_${empName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Reference PDF downloaded');
    } catch (error) {
      console.error('PDF download failed:', error);
      toast.error('Failed to download reference PDF');
    } finally {
      setDownloadingPdf(null);
    }
  };
  
  // Open verify/reject modal
  const openVerifyDialog = (refNum, action = 'verify') => {
    const currentRef = references?.references?.[`reference_${refNum}`] || {};
    if (referenceIsFinalised(currentRef)) {
      toast.info('This reference is already satisfactory. Use the audited reset/change flow if it needs to be changed.');
      return;
    }
    setVerifyRefNum(refNum);
    setVerifyAction(action);
    setVerifyNotes('');
    setMismatchReason('');
    // Seed reference type from stored value or infer from relationship_type.
    const storedType = currentRef.type;
    const rel = (currentRef.response?.relationship_type || '').toLowerCase();
    const seededType = storedType
      || (rel && (rel.includes('manager') || rel.includes('supervisor') || rel.includes('director') || rel.includes('hr'))
          ? 'employment'
          : rel.includes('tutor') || rel.includes('lecturer') || rel.includes('university')
          ? 'academic'
          : 'employment'); // default to employment — admin can switch
    setReferenceType(seededType);
    setExplanationReason(currentRef.explanation_reason || '');
    setExplanationRequired(false);
    setVerifyDialogOpen(true);
  };
  
  // Handle verify/reject
  const handleVerifyReference = async () => {
    setVerifyLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/employees/${employeeId}/references/${verifyRefNum}/verify`,
        {
          action: verifyAction,
          notes: verifyNotes,
          mismatch_reason: mismatchReason || null,
          // Reg 19 sufficiency
          reference_type: verifyAction === 'verify' ? referenceType : null,
          is_employment_reference: verifyAction === 'verify' ? referenceType === 'employment' : null,
          explanation_reason: verifyAction === 'verify' && explanationReason ? explanationReason : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const nextStatus = verifyAction === 'verify' ? 'verified' : 'rejected';

      setReferences(prev => {
        if (!prev?.references) return prev;

        const key = `reference_${verifyRefNum}`;
        const currentRef = prev.references[key] || {};

        return {
          ...prev,
          references: {
            ...prev.references,
            [key]: {
              ...currentRef,
              status: nextStatus,
              request: {
                ...(currentRef.request || {}),
                status: nextStatus
              },
              verification: {
                ...(currentRef.verification || {}),
                status: nextStatus,
                verified: verifyAction === 'verify',
                verified_at:
                  verifyAction === 'verify'
                    ? new Date().toISOString()
                    : currentRef.verification?.verified_at,
                rejected_at:
                  verifyAction === 'reject'
                    ? new Date().toISOString()
                    : currentRef.verification?.rejected_at,
                rejection_reason:
                  verifyAction === 'reject'
                    ? verifyNotes
                    : currentRef.verification?.rejection_reason,
                notes:
                  verifyAction === 'verify'
                    ? verifyNotes
                    : currentRef.verification?.notes
              },
              ...(verifyAction === 'reject'
                ? {
                    declared: {},
                    response: {},
                    review: {},
                    mismatch: {}
                  }
                : {})
            }
          }
        };
      });

      toast.success(
        verifyAction === 'verify'
          ? 'Reference verified successfully'
          : 'Reference rejected'
      );

      setReviewDialogOpen(false);
      setVerifyDialogOpen(false);
      setVerifyRefNum(null);
      setVerifyNotes('');
      setMismatchReason('');

      await fetchReferences();
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      // CQC Reg 19 sufficiency: backend demands an explanation before we can
      // mark this reference verified. Keep the dialog open and reveal the
      // explanation field.
      const detail = error?.response?.data?.detail;
      const requiresExplanation =
        detail && typeof detail === 'object' && detail.requires_explanation === true;
      if (requiresExplanation) {
        setExplanationRequired(true);
        toast.error(
          detail.message ||
            'This role requires at least one employment reference. Please provide an explanation for the alternative reference.'
        );
      } else {
        toast.error(extractErrorMessage(error, `Failed to ${verifyAction} reference`));
      }
    } finally {
      setVerifyLoading(false);
    }
  };

  // Flag reference as not from most recent employer
  const handleFlagRecentEmployer = async (refNum) => {
    setFlaggingMismatch(refNum);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/employees/${employeeId}/references/${refNum}/flag-recent-employer-mismatch`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.status === 'not_required') {
        toast.info(res.data.message || 'No flag needed — most recent employer is already covered by another reference.');
      } else {
        toast.success('Mismatch flagged — worker will see an explanation task on their dashboard.');
      }
      fetchReferences();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to flag mismatch'));
    } finally {
      setFlaggingMismatch(null);
    }
  };

  // Admin reviews worker's mismatch explanation
  const openReviewExplanation = (refNum) => {
    setReviewExplanationRef(refNum);
    setReviewDecision('');
    setReviewNotes('');
    setReviewExplanationOpen(true);
  };

  const handleReviewExplanation = async () => {
    if (!reviewDecision) {
      toast.error('Please select a decision');
      return;
    }
    setReviewingExplanation(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/references/${employeeId}/${reviewExplanationRef}/review-mismatch-explanation`,
        { decision: reviewDecision, admin_notes: reviewNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Explanation ${reviewDecision}.`);
      setReviewExplanationOpen(false);
      fetchReferences();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to review explanation'));
    } finally {
      setReviewingExplanation(false);
    }
  };

  // A previously declared referee with an unmatched employer needs a
  // documented explanation, not a duplicate or a rejection.
  const openReasonDialog = (refNum) => {
    const declared = references?.references?.[`reference_${refNum}`]?.declared || {};
    setReasonRefNum(refNum);
    setReasonType(declared.declaration_reason_type || '');
    setReasonNote(declared.declaration_reason_note || '');
    setReasonDialogOpen(true);
  };

  const handleSaveReason = async () => {
    if (!reasonRefNum || !reasonType) {
      toast.error('Select why this referee is appropriate.');
      return;
    }
    if (reasonType === 'other' && reasonNote.trim().length < 10) {
      toast.error('Add a short explanation of at least 10 characters.');
      return;
    }
    setSavingReason(true);
    try {
      const token = localStorage.getItem('token');
      const result = await axios.patch(
        `${API}/employees/${employeeId}/references/${reasonRefNum}/declaration-reason`,
        { reason_type: reasonType, reason_note: reasonNote.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(result.data?.message || 'Explanation recorded for manager review.');
      setReasonDialogOpen(false);
      await fetchReferences();
      if (onRefresh) await onRefresh();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Could not save the referee explanation.'));
    } finally {
      setSavingReason(false);
    }
  };

  const openAddDialog = (refNum) => {
    setAddRefNum(refNum);
    setRefereeForm({
      name: '',
      email: '',
      phone: '',
      organisation: '',
      position: '',
      relationship: '',
      slot_reason_type: '',
      slot_reason_note: '',
    });
    setAddDialogOpen(true);
  };
  
  // Safely extract a displayable error message from axios/FastAPI errors
  const extractErrorMessage = (error, fallback = 'An error occurred') => {
    const detail = error.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map(e => (typeof e === 'object' ? (e.msg || JSON.stringify(e)) : String(e))).join('; ');
    }
    if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
    return String(detail);
  };

  const handleAddReferee = async () => {
    // Validate ref num
    if (!addRefNum || ![1, 2].includes(addRefNum)) {
      toast.error('Invalid reference number. Please close and try again.');
      return;
    }

    // Validate
    if (!refereeForm.name.trim() || !refereeForm.email.trim() || !refereeForm.organisation.trim()) {
      toast.error('Name, email, and organisation are required');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(refereeForm.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...refereeForm,
        slot_reason_type: refereeForm.slot_reason_type || null,
        slot_reason_note: refereeForm.slot_reason_note || null,
      };
      const response = await axios.post(
        `${API}/employees/${employeeId}/references/${addRefNum}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const warnings = response.data?.declaration_warnings || [];
      if (warnings.length > 0) {
        toast.warning(warnings.map((w) => w.message || w).join(' '));
      }
      toast.success(`Referee ${addRefNum} details added successfully`);
      setAddDialogOpen(false);
      fetchReferences();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to add referee'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-[#E4E8EB] shadow-sm">
        <CardContent className="py-12 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (loadError || !references) {
    return (
      <Card className="border-[#E4E8EB] shadow-sm">
        <CardContent className="py-8 text-center text-red-700">
          <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-red-500" />
          <p className="font-medium">Cannot assess references</p>
          <p className="text-sm text-red-600 mt-1">Reference data unavailable. Verification actions are disabled until this source loads.</p>
          <Button variant="outline" size="sm" onClick={fetchReferences} className="mt-4 rounded-lg">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const referenceValues = [1, 2].map((refNum) => references.references?.[`reference_${refNum}`] || {});
  const displayStatuses = referenceValues.map(deriveDisplayStatus);

  // ── Readiness counts ──
  // Backend is the source of truth. A reference only counts toward readiness
  // when `counts_toward_readiness === true` (verified AND no unresolved mismatch).
  // Never infer satisfactory from the raw "verified" status — that allowed the
  // contradictory "2/2 satisfactory" + "mismatch investigation required" state.
  const satisfactoryCount = referenceValues.filter((r) => r && r.counts_toward_readiness === true).length;
  const missingCount = displayStatuses.filter((s) => s === 'not_declared').length;
  const requestNotSentCount = displayStatuses.filter((s) => s === 'declared').length;
  const awaitingResponseCount = displayStatuses.filter((s) => s === 'sent').length;
  const awaitingAdminReviewCount = displayStatuses.filter((s) => s === 'response_received').length;
  const rejectedCount = displayStatuses.filter((s) => s === 'rejected').length;
  const legacyUnverifiedCount = displayStatuses.filter((s) => s === 'legacy_unverified').length;
  const mismatchCount = displayStatuses.filter((s) => s === 'mismatch').length;
  const cannotAssessCount = legacyUnverifiedCount;
  const hasBlockers = satisfactoryCount < 2;

  // Summary banner colour
  const summaryBorderClass = satisfactoryCount >= 2
    ? 'border-green-200 bg-green-50'
    : (rejectedCount > 0 || cannotAssessCount > 0 || mismatchCount > 0)
      ? 'border-red-200 bg-red-50'
      : 'border-amber-200 bg-amber-50';
  const summaryTextClass = satisfactoryCount >= 2
    ? 'text-green-800'
    : (rejectedCount > 0 || cannotAssessCount > 0 || mismatchCount > 0)
      ? 'text-red-800'
      : 'text-amber-800';

  return (
    <>
      <Card className="border-[#E4E8EB] shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center justify-between">
            <span className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              Employment References
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchReferences}
              disabled={loading}
              className="rounded-xl"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            This workspace requires at least 2 satisfactory professional references before work readiness.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {workflowError && (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-sm text-amber-900">
                CareOps could not refresh the referee action checks. Saved referee details have not changed.
              </p>
              <Button size="sm" variant="outline" onClick={fetchReferences}>
                <RefreshCw className="mr-1 h-4 w-4" /> Try again
              </Button>
            </div>
          )}
          {/* ── Top summary banner ── */}
          <div className={`rounded-xl border p-4 ${summaryBorderClass}`}>
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                {satisfactoryCount >= 2
                  ? <CheckCircle className="h-5 w-5 text-green-600" />
                  : <AlertTriangle className="h-5 w-5 text-amber-600" />}
              </div>
              <div className="flex-1">
                <p className={`font-medium ${summaryTextClass}`}>
                  {satisfactoryCount} of 2 references satisfactory
                </p>
                {hasBlockers && (
                  <p className="mt-1 text-sm text-slate-700" data-testid="references-next-action">
                    {missingCount > 0 ? `${missingCount} referee${missingCount === 1 ? '' : 's'} need to be provided.`
                      : requestNotSentCount > 0 ? `Send ${requestNotSentCount} outstanding referee request${requestNotSentCount === 1 ? '' : 's'} to start collecting evidence.`
                      : awaitingResponseCount > 0 ? `Waiting for ${awaitingResponseCount} referee response${awaitingResponseCount === 1 ? '' : 's'}.`
                      : awaitingAdminReviewCount > 0 ? `${awaitingAdminReviewCount} returned reference${awaitingAdminReviewCount === 1 ? '' : 's'} need review.`
                      : rejectedCount > 0 || mismatchCount > 0 || cannotAssessCount > 0 ? 'A referee needs a decision, replacement or evidence review.'
                      : 'Complete the remaining verified reference checks.'}
                  </p>
                )}
              </div>
            </div>
          </div>
          {/* Reference Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[1, 2].map(refNum => {
              const ref = references.references?.[`reference_${refNum}`];
              const declared = ref?.declared || {};
              const request = ref?.request || {};
              const response = ref?.response || {};
              const verification = ref?.verification || {};
              const replacementRequested = Boolean(ref?.replacement_requested_at || request?.replacement_requested_at);
              const rawStatus = ref?.status || 'not_declared';
              const displayStatus = deriveDisplayStatus(ref);
              const config = STATUS_CONFIG[displayStatus] || STATUS_CONFIG.not_declared;
              const StatusIcon = config.icon;
              const hasCanonicalResponse = Boolean(
                ref?.response && typeof ref.response === 'object' && Object.keys(ref.response).length > 0
              );

              return (
                <div 
                  key={refNum} 
                  className="border rounded-xl overflow-hidden"
                  data-testid={`reference-${refNum}-card`}
                >
                  {/* Header */}
                  <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-b">
                    <h3 className="font-medium flex items-center gap-2">
                      Reference {refNum}
                    </h3>
                    <div className="flex items-center gap-2">
                      {declared.name && !replacementRequested && onEditReference && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEditReference(refNum, declared)}
                          className="h-7 px-2 text-gray-500 hover:text-primary"
                          data-testid={`edit-reference-btn-${refNum}`}
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Badge className={`${config.color} flex items-center gap-1`}>
                        <StatusIcon className="h-3 w-3" />
                        {config.label}
                      </Badge>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4 space-y-4">
                    {replacementRequested && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2" data-testid={`reference-replacement-pending-${refNum}`}>
                        <p className="text-xs font-medium text-amber-800">
                          Replacement requested: waiting for worker to provide new referee details.
                        </p>
                      </div>
                    )}
                    {!replacementRequested && (displayStatus === 'rejected' || displayStatus === 'mismatch') && (
                      <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2" data-testid={`reference-action-required-${refNum}`}>
                        <p className="text-xs font-medium text-rose-800">
                          This referee does not currently count toward readiness. Ask worker to provide a replacement referee with reason.
                        </p>
                      </div>
                    )}
                    {/* Declared Info */}
                    {declared.name && !replacementRequested ? (
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <User className="h-5 w-5 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900">{declared.name}</p>
                            {declared.job_title && (
                              <p className="text-sm text-gray-600 flex items-center gap-1">
                                <Briefcase className="h-3 w-3" />
                                {declared.job_title}
                              </p>
                            )}
                            {declared.organisation && (
                              <p className="text-sm text-gray-600 flex items-center gap-1">
                                <Building className="h-3 w-3" />
                                {declared.organisation}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Contact Details */}
                        <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                          {declared.email && (
                            <p className="text-sm flex items-center gap-2 text-gray-600">
                              <Mail className="h-3.5 w-3.5 text-gray-400" />
                              <a href={`mailto:${declared.email}`} className="hover:text-primary">
                                {declared.email}
                              </a>
                            </p>
                          )}
                          {declared.phone && (
                            <p className="text-sm flex items-center gap-2 text-gray-600">
                              <Phone className="h-3.5 w-3.5 text-gray-400" />
                              {declared.phone}
                            </p>
                          )}
                          {declared.relationship && (
                            <p className="text-sm text-gray-500">
                              Relationship: {declared.relationship}
                            </p>
                          )}
                          {declared.years_known && (
                            <p className="text-sm text-gray-500">
                              Known for: {declared.years_known} years
                            </p>
                          )}
                          {declared.is_professional !== undefined && (
                            <Badge variant="outline" className="text-xs mt-1">
                              {declared.is_professional ? 'Professional Reference' : 'Personal Reference'}
                            </Badge>
                          )}
                        </div>

                        {displayStatus === 'declared'
                          && !ref?.request?.sent_at
                          && !ref?.has_canonical_response
                          && ref?.can_send_reference_request === false
                          && (ref?.send_blockers?.length > 0) && (
                          <div
                            className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
                            data-testid={`reference-send-blockers-${refNum}`}
                          >
                            <p className="text-xs font-medium text-amber-800 flex items-center gap-1">
                              <AlertTriangle className="h-3.5 w-3.5" />
                              Cannot send reference request yet
                            </p>
                            <ul className="mt-1 text-xs text-amber-700 list-disc list-inside space-y-0.5">
                              {ref.send_blockers.map((blocker, idx) => (
                                <li key={idx}>{blocker}</li>
                              ))}
                            </ul>
                            {ref.send_blockers.some(blocker =>
                              /organisation|employment history|most recent employer|appropriate|reason/i.test(String(blocker))
                            ) && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="mt-3 w-full border-amber-300 text-amber-900"
                                onClick={() => openReasonDialog(refNum)}
                                data-testid={`reference-correct-declaration-btn-${refNum}`}
                              >
                                <Edit className="h-4 w-4 mr-2" />
                                Review referee choice
                              </Button>
                            )}
                          </div>
                        )}

                        {/*
                          ── Admin actions ──
                          Gating is driven by explicit backend capabilities, NOT by the
                          display badge. This keeps the admin operable when the ref is
                          in the 'mismatch' display state (raw status is 'verified' but
                          counts_toward_readiness is false): they can still review the
                          response, review the worker explanation, or reject.
                        */}
                        {declared.email && (() => {
                          // Canonical action gating: UI buttons must follow backend
                          // `allowed_actions` so admin and worker state stay in lockstep.
                          // Keep a narrow legacy fallback only when this list is missing.
                          const allowedActions = Array.isArray(ref?.allowed_actions) ? ref.allowed_actions : [];
                          const allowed = new Set(allowedActions.map((a) => String(a || '').toLowerCase()));
                          const hasCanonicalAllowedActions = allowed.size > 0;
                          const finalisedReference = referenceIsFinalised(ref);
                          const canSendRequest = !finalisedReference && !hasCanonicalResponse
                            && ref?.can_send_reference_request !== false
                            && (ref?.can_send_reference_request === true || (hasCanonicalAllowedActions
                            ? (
                                allowed.has('send_request')
                                || allowed.has('resend_request')
                                || allowed.has('re_request')
                              )
                            : (
                                !hasCanonicalResponse
                                && !ref?.verification?.verified_at
                                && ref?.status !== 'rejected'
                                && (displayStatus === 'declared' || displayStatus === 'sent' || displayStatus === 'legacy_unverified')
                              )));
                          const canReviewResponse = !finalisedReference && (hasCanonicalAllowedActions
                            ? allowed.has('review_response')
                            : Boolean(ref?.can_review_response ?? hasCanonicalResponse));
                          const canVerify = !finalisedReference && hasCanonicalResponse && (hasCanonicalAllowedActions
                            ? (allowed.has('mark_satisfactory') || allowed.has('verify_reference'))
                            : Boolean(ref?.can_verify_reference));
                          const canReject = !finalisedReference && hasCanonicalResponse && displayStatus !== 'verified' && (
                            hasCanonicalAllowedActions
                              ? (allowed.has('mark_unsatisfactory') || allowed.has('reject_reference'))
                              : Boolean(ref?.can_reject_reference ?? (ref?.status !== 'rejected'))
                          );
                          const canReviewMismatch = !finalisedReference && (
                            hasCanonicalAllowedActions
                              ? allowed.has('review_mismatch_explanation')
                              : Boolean(ref?.can_review_mismatch_explanation)
                          );
                          const showActionBar = canSendRequest || canReviewResponse || canVerify || canReject || canReviewMismatch;
                          if (!showActionBar) return null;
                          return (
                            <div className="pt-2 border-t space-y-2">
                              {canSendRequest && (
                                <Button
                                  size="sm"
                                  className="w-full rounded-lg"
                                  onClick={() => openSendDialog(refNum)}
                                  disabled={sendingRequest === refNum}
                                  data-testid={`send-request-btn-${refNum}`}
                                >
                                  {sendingRequest === refNum ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  ) : (
                                    <Send className="h-4 w-4 mr-2" />
                                  )}
                                  {displayStatus === 'sent' ? 'Resend Request'
                                    : displayStatus === 'legacy_unverified' ? 'Re-request Reference'
                                    : 'Send reference request'}
                                </Button>
                              )}
                              {canReviewResponse && (!canVerify && !canReject ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full rounded-lg"
                                  onClick={() => openReviewDialog(refNum)}
                                  data-testid={`review-response-btn-${refNum}`}
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  Review Response
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full rounded-lg"
                                  onClick={() => openReviewDialog(refNum)}
                                  data-testid={`review-response-btn-${refNum}`}
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  Review Response
                                </Button>
                              ))}
                              {(canVerify || canReject) && (
                                <div className="flex gap-2">
                                  {canVerify && (
                                    <Button
                                      size="sm"
                                      className="flex-1 rounded-lg bg-green-600 hover:bg-green-700"
                                      onClick={() => openVerifyDialog(refNum, 'verify')}
                                      data-testid={`verify-reference-btn-${refNum}`}
                                    >
                                      <CheckCircle className="h-4 w-4 mr-2" />
                                      Mark Satisfactory
                                    </Button>
                                  )}
                                  {canReject && (
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      className="flex-1 rounded-lg"
                                      onClick={() => openVerifyDialog(refNum, 'reject')}
                                      data-testid={`reject-reference-btn-${refNum}`}
                                    >
                                      <XCircle className="h-4 w-4 mr-2" />
                                      Unsatisfactory
                                    </Button>
                                  )}
                                </div>
                              )}
                              {canReviewMismatch && (
                                (() => {
                                  const reviewTypeLabel = getMismatchTypeLabel(ref?.integrity?.mismatch_explanation_type);
                                  return (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full rounded-lg border-amber-300 text-amber-800 hover:bg-amber-50"
                                  onClick={() => openReviewExplanation(refNum)}
                                  data-testid={`review-explanation-inline-btn-${refNum}`}
                                >
                                  <MessageSquare className="h-4 w-4 mr-2" />
                                  {`Review ${reviewTypeLabel}`}
                                </Button>
                                  );
                                })()
                              )}
                              {displayStatus === 'response_received' && !hasCanonicalResponse && (
                                <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                                  <p className="text-xs text-amber-700">
                                    Response status set but no canonical response data found. Re-request or manually set response source before review.
                                  </p>
                                </div>
                              )}
                            </div>
                          );
                        })()}


                        {declared.email
                          && !referenceIsFinalised(ref)
                          && (ref?.needs_employment_review
                            || (ref?.declaration_warnings || []).some(issue =>
                              /employer|employment history|reason|organisation/i.test(
                                String(issue?.message || issue || '')
                              )
                            )) && (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3"
                              data-testid={`reference-review-notice-${refNum}`}>
                              <p className="text-sm font-medium text-amber-900">
                                Employment-history difference needs review
                              </p>
                              <p className="mt-1 text-xs text-amber-800">
                                You can contact this referee now. Before the returned response counts,
                                check the relationship and document why this source is appropriate.
                              </p>
                              <Button size="sm" variant="outline" className="mt-2 border-amber-300"
                                onClick={() => openReasonDialog(refNum)}
                                data-testid={`reference-record-reason-btn-${refNum}`}>
                                <Edit className="h-4 w-4 mr-1" />
                                Record explanation
                              </Button>
                            </div>
                          )}

                        {/* ── Stage Pipeline ── */}
                        <details className="rounded-lg border border-slate-200 px-3 py-2" data-testid={`reference-evidence-history-${refNum}`}>
                          <summary className="cursor-pointer text-xs font-semibold text-slate-600">
                            Evidence stages &amp; history
                          </summary>
                          <div className="mt-2 space-y-1.5">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Evidence stages</p>
                          {/* Stage 1: Referee declared */}
                          <div className="flex items-center gap-2 text-xs">
                            <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />
                            <span className="text-gray-700">Referee declared</span>
                          </div>
                          {/* Stage 2: Request sent */}
                          <div className="flex items-center gap-2 text-xs">
                            {request.sent_at
                              ? <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />
                              : <Clock className="h-3.5 w-3.5 text-gray-300 shrink-0" />}
                            <span className={request.sent_at ? 'text-gray-700' : 'text-gray-400'}>
                              {request.sent_at
                                ? `Request sent ${formatBackendDate(request.sent_at)}`
                                : 'Request not sent'}
                            </span>
                          </div>
                          {/* Stage 3: Response received */}
                          <div className="flex items-center gap-2 text-xs">
                            {hasCanonicalResponse
                              ? <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />
                              : <Clock className="h-3.5 w-3.5 text-gray-300 shrink-0" />}
                            <span className={hasCanonicalResponse ? 'text-gray-700' : 'text-gray-400'}>
                              {hasCanonicalResponse
                                ? `Response received${ref?.response_submitted_at || ref?.response?.submitted_at ? ` ${formatBackendDate(ref.response_submitted_at || ref.response.submitted_at)}` : ''}`
                                : 'No response yet'}
                            </span>
                          </div>
                          {/* Stage 4: Admin reviewed */}
                          <div className="flex items-center gap-2 text-xs">
                            {(displayStatus === 'verified' || displayStatus === 'rejected' || displayStatus === 'mismatch')
                              ? <CheckCircle className={`h-3.5 w-3.5 shrink-0 ${displayStatus === 'verified' ? 'text-green-500' : displayStatus === 'rejected' ? 'text-red-500' : 'text-amber-500'}`} />
                              : <Clock className="h-3.5 w-3.5 text-gray-300 shrink-0" />}
                            <span className={(displayStatus === 'verified' || displayStatus === 'rejected' || displayStatus === 'mismatch') ? 'text-gray-700' : 'text-gray-400'}>
                              {displayStatus === 'verified'
                                ? `Satisfactory${verification.verified_by ? ` — ${verification.verified_by}` : ''}${verification.verified_at ? ` on ${formatBackendDate(verification.verified_at)}` : ''}`
                                : displayStatus === 'rejected'
                                  ? 'Unsatisfactory / action required'
                                  : displayStatus === 'mismatch'
                                    ? 'Reviewed — blocked by unresolved mismatch'
                                    : 'Awaiting admin review'}
                            </span>
                          </div>
                          </div>
                        </details>

                        {/* Legacy data warning */}
                        {displayStatus === 'legacy_unverified' && (
                          <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                            <p className="text-sm font-medium text-amber-700 flex items-center gap-2">
                              <AlertTriangle className="h-4 w-4" />
                              Cannot assess — response evidence not found
                            </p>
                            <p className="text-xs text-amber-600 mt-1">
                              Legacy employee record shows this reference as verified, but no canonical response trail exists. Re-request or manually record the response source before marking satisfactory.
                            </p>
                          </div>
                        )}

                        {/* Response detail view (only when canonical response exists AND admin has not stamped) */}
                        {hasCanonicalResponse && displayStatus !== 'verified' && !ref?.verification?.verified_at && (
                          <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-purple-700 flex items-center gap-2">
                                <MessageSquare className="h-4 w-4" />
                                Response received — awaiting admin review
                              </p>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-purple-700 hover:text-purple-900 hover:bg-purple-100"
                                onClick={() => openReviewDialog(refNum)}
                                data-testid={`view-response-btn-${refNum}`}
                              >
                                <Eye className="h-3.5 w-3.5 mr-1" />
                                View Full Response
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Verification status (satisfactory) — only when truly satisfactory */}
                        {displayStatus === 'verified' && (
                          <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                            <p className="text-sm font-medium text-green-700 flex items-center gap-2">
                              <CheckCircle className="h-4 w-4" />
                              Satisfactory
                            </p>
                            {verification.verified_by && (
                              <p className="text-xs text-green-600 mt-1">
                                By: {verification.verified_by}
                              </p>
                            )}
                            {verification.verified_at && (
                              <p className="text-xs text-green-600">
                                On: {formatBackendDate(verification.verified_at)}
                              </p>
                            )}
                          </div>
                        )}

                        {/* ── Mismatch panel ──
                            Suppressed when admin has already requested a
                            replacement referee for this slot — the previous
                            mismatch is no longer the action item; the
                            action item is "provide new referee details".
                            Avoids contradictory UI like "Re-explain"
                            alongside "Provide New Referee". */}
                        {ref?.integrity?.mismatch_detected && !ref?.replacement_requested_at && (
                          <div className="mt-3 space-y-2">
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                              <p className="text-xs font-semibold text-amber-800 flex items-center gap-1 mb-1">
                                <AlertTriangle className="h-3.5 w-3.5" />
                                Mismatch Detected
                              </p>
                              <p className="text-xs text-amber-700">
                                {ref.integrity.mismatch_notes ||
                                  (ref.integrity.mismatch_reasons || []).join(' · ') ||
                                  'Reference discrepancy detected.'}
                              </p>
                            </div>

                            {/* Worker explanation (if submitted) */}
                            {ref.integrity.mismatch_explanation && (
                              <div className={`p-3 border rounded-lg ${
                                ref.integrity.mismatch_admin_decision === 'accepted'
                                  ? 'bg-green-50 border-green-200'
                                  : ref.integrity.mismatch_admin_decision === 'rejected'
                                  ? 'bg-red-50 border-red-200'
                                  : 'bg-blue-50 border-blue-200'
                              }`}>
                                <p className="text-xs font-semibold text-slate-700 mb-1">
                                  Worker Explanation{' '}
                                  <span className="text-slate-400 font-normal">
                                    ({getMismatchTypeLabel(ref.integrity.mismatch_explanation_type || 'other')})
                                  </span>
                                </p>
                                <p className="text-xs text-slate-600 mb-2">{ref.integrity.mismatch_explanation}</p>

                                {ref.integrity.mismatch_admin_decision ? (
                                  <p className={`text-xs font-medium ${
                                    ref.integrity.mismatch_admin_decision === 'accepted'
                                      ? 'text-green-700'
                                      : 'text-red-700'
                                  }`}>
                                    Decision: {ref.integrity.mismatch_admin_decision}
                                    {ref.integrity.mismatch_admin_notes && ` — ${ref.integrity.mismatch_admin_notes}`}
                                  </p>
                                ) : (
                                  (() => {
                                    const reviewTypeLabel = getMismatchTypeLabel(ref?.integrity?.mismatch_explanation_type);
                                    return (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-xs h-7"
                                    onClick={() => openReviewExplanation(refNum)}
                                    data-testid={`review-explanation-btn-${refNum}`}
                                  >
                                    <MessageSquare className="h-3 w-3 mr-1" />
                                    {`Review ${reviewTypeLabel}`}
                                  </Button>
                                    );
                                  })()
                                )}
                              </div>
                            )}

                            {!ref.integrity.mismatch_explanation && (
                              <p className="text-xs text-amber-600 italic">
                                Awaiting worker {getMismatchTypeLabel(ref.integrity.mismatch_explanation_type)} explanation via dashboard task.
                              </p>
                            )}
                          </div>
                        )}

                        {/* Flag: not from most recent employer (only if no mismatch already) */}
                        {declared.name && !ref?.integrity?.mismatch_detected && (
                          <div className="pt-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-xs text-slate-500 hover:text-amber-600 w-full justify-start"
                              onClick={() => handleFlagRecentEmployer(refNum)}
                              disabled={flaggingMismatch === refNum}
                              data-testid={`flag-recent-employer-btn-${refNum}`}
                            >
                              {flaggingMismatch === refNum ? (
                                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                              ) : (
                                <AlertTriangle className="h-3.5 w-3.5 mr-1" />
                              )}
                              Flag: not from most recent employer
                            </Button>
                          </div>
                        )}

                        {/* ── Decision summary + history ─────────────────
                            Admin visibility pass: show (a) the canonical
                            one-line reason from the backend explaining why
                            this reference does / does not currently count
                            toward readiness, and (b) an expandable audit
                            history of past reject / replacement actions
                            (from db.references.ref{n}.history[], built by
                            _build_reference_history_entry). Display-only —
                            no truth logic is derived here. */}
                        {declared.name && (ref?.counts_toward_readiness_reason || (ref?.history && ref.history.length > 0)) && (
                          <div className="pt-2 border-t border-slate-100 space-y-2" data-testid={`reference-history-${refNum}`}>
                            {ref?.counts_toward_readiness_reason && (
                              <div className="flex items-start gap-2 text-xs">
                                {ref.counts_toward_readiness ? (
                                  <CheckCircle className="h-3.5 w-3.5 text-green-600 mt-0.5 flex-shrink-0" />
                                ) : (
                                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                                )}
                                <span className="text-slate-600">
                                  <span className="font-medium text-slate-700">
                                    {ref.counts_toward_readiness ? 'Counts toward readiness.' : 'Does not count toward readiness.'}
                                  </span>{' '}
                                  {ref.counts_toward_readiness_reason}
                                </span>
                              </div>
                            )}
                            {ref?.history && ref.history.length > 0 && (
                              <details className="group" data-testid={`reference-history-toggle-${refNum}`}>
                                <summary className="cursor-pointer text-xs font-medium text-slate-600 hover:text-primary flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  Decision history ({ref.history.length})
                                </summary>
                                <ol className="mt-2 space-y-2 pl-4 border-l-2 border-slate-200">
                                  {[...ref.history]
                                    .sort((a, b) => {
                                      const at = a?.recorded_at || '';
                                      const bt = b?.recorded_at || '';
                                      return bt.localeCompare(at); // newest first
                                    })
                                    .map((entry, idx) => {
                                      const action = entry?.action || 'update';
                                      const actionLabel = action === 'reject'
                                        ? 'Rejected'
                                        : action === 'request_replacement'
                                          ? 'Replacement requested'
                                          : action.replace(/_/g, ' ');
                                      const actionCls = action === 'reject'
                                        ? 'text-red-700'
                                        : action === 'request_replacement'
                                          ? 'text-amber-700'
                                          : 'text-slate-700';
                                      const reason = entry?.action_reason;
                                      const when = entry?.recorded_at ? formatBackendDateTime(entry.recorded_at) : null;
                                      const workerExpl = entry?.worker_explanation;
                                      const adminPrior = entry?.admin_prior_decision;
                                      const mismatchSnap = entry?.mismatch;
                                      return (
                                        <li
                                          key={`${entry?.recorded_at || idx}-${idx}`}
                                          className="text-xs"
                                          data-testid={`reference-history-entry-${refNum}`}
                                        >
                                          <div className="flex items-center gap-2">
                                            <span className={`font-medium ${actionCls}`}>{actionLabel}</span>
                                            {when && <span className="text-slate-400">· {when}</span>}
                                          </div>
                                          {reason && (
                                            <p className="text-slate-600 mt-0.5">Reason: {reason}</p>
                                          )}
                                          {mismatchSnap?.detected && (
                                            <p className="text-slate-500 mt-0.5">
                                              Mismatch at time of action{mismatchSnap.reason ? `: ${mismatchSnap.reason}` : ''}
                                              {mismatchSnap.resolved ? ' (resolved)' : ''}
                                            </p>
                                          )}
                                          {workerExpl?.submitted && workerExpl?.text && (
                                            <p className="text-slate-500 mt-0.5 italic">
                                              Worker explanation ({workerExpl.type || 'other'}): “{workerExpl.text}”
                                            </p>
                                          )}
                                          {adminPrior?.decision && (
                                            <p className="text-slate-500 mt-0.5">
                                              Prior admin decision: {adminPrior.decision}
                                              {adminPrior.notes ? ` — ${adminPrior.notes}` : ''}
                                            </p>
                                          )}
                                        </li>
                                      );
                                    })}
                                </ol>
                              </details>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-amber-400" />
                        <p className="text-gray-600 font-medium">No referee declared for Reference {refNum}</p>
                        <p className="text-xs text-gray-500 mt-1 mb-4">Add referee details manually or extract from application form</p>
                        <Button
                          size="sm"
                          onClick={() => openAddDialog(refNum)}
                          className="rounded-lg"
                          data-testid={`add-referee-btn-${refNum}`}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Referee Details
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Auditable requirements remain available without filling the
              everyday operator screen with repeated compliance instructions. */}
          <details className="rounded-lg border border-slate-200 p-3" data-testid="reference-policy-details">
            <summary className="cursor-pointer text-sm font-medium text-slate-700">
              Reference policy and evidence requirements
            </summary>
            <div className="pt-2">
              <p className="mb-2 text-xs text-slate-600">
                The agency’s two-satisfactory-reference readiness policy operates alongside
                applicable employment-business rules. For roles involving vulnerable people,
                the Conduct Regulations require steps to obtain two non-relative written
                references and documented disclosure to the hirer if reasonable efforts fail.
                Do not mistake a referee declaration for a verified response.
              </p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>- Minimum 2 satisfactory professional references required</li>
              <li>- Each must have a canonical response on file (not just declared or legacy-verified)</li>
              <li>- Each must be reviewed and marked satisfactory by an admin</li>
              <li>- At least one must match the most recent declared employer</li>
              <li>- A second reference can be from an earlier employer or alternative source only with documented explanation</li>
              <li>- Declared referee details alone do not count as a satisfactory reference</li>
            </ul>
            </div>
          </details>
        </CardContent>
      </Card>

      {/* Fix for previously declared referees with unmatched employer names.
          The applicant's original declaration and evidence stay intact. */}
      <Dialog open={reasonDialogOpen} onOpenChange={setReasonDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Explain Reference {reasonRefNum}</DialogTitle>
            <DialogDescription>
              Record the genuine reason this referee is appropriate if their employer
              or relationship differs from the applicant's history. This can be done
              before or after contacting them. It never marks the response satisfactory.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div>
              <Label>Why is this referee appropriate?</Label>
              <Select value={reasonType || undefined} onValueChange={setReasonType}>
                <SelectTrigger data-testid="existing-referee-reason-type">
                  <SelectValue placeholder="Choose a reason" />
                </SelectTrigger>
                <SelectContent>
                  {SLOT_REASON_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {reasonType && (
              <div>
                <Label>Explanation {reasonType === 'other' ? '(required)' : '(recommended)'}</Label>
                <Textarea
                  value={reasonNote}
                  onChange={event => setReasonNote(event.target.value)}
                  rows={3}
                  placeholder="For example, the referee supervised the applicant during an agency placement."
                  data-testid="existing-referee-reason-note"
                />
              </div>
            )}
            <p className="text-xs text-slate-600">
              Recording an explanation does not send a request or approve evidence.
              A professional referee still needs to provide a genuine response
              and pass normal review. A family or personal reference is not
              automatically acceptable for a professional reference requirement.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReasonDialogOpen(false)}>Cancel</Button>
            <Button
              disabled={savingReason || !reasonType || (reasonType === 'other' && reasonNote.trim().length < 10)}
              onClick={handleSaveReason}
              data-testid="existing-referee-reason-save"
            >
              {savingReason ? 'Saving…' : 'Save reason'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Request Dialog */}
      <Dialog
        open={sendDialogOpen}
        onOpenChange={(open) => {
          setSendDialogOpen(open);
          if (!open) {
            setManualWhatsAppUrl(null);
            setCustomMessage('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Send Reference Request</DialogTitle>
            <DialogDescription>
              Choose how to send the same secure reference request to this referee.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedRef && references.references?.[`reference_${selectedRef}`]?.declared && (() => {
              const declared = references.references[`reference_${selectedRef}`].declared;
              return (
                <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                  <p className="font-medium">{declared.name}</p>
                  {declared.email && (
                    <p className="text-sm text-gray-600 flex items-center gap-2">
                      <Mail className="h-3.5 w-3.5" />
                      {declared.email}
                    </p>
                  )}
                  {declared.phone && (
                    <p className="text-sm text-gray-600 flex items-center gap-2">
                      <Phone className="h-3.5 w-3.5" />
                      {declared.phone}
                    </p>
                  )}
                </div>
              );
            })()}
            {selectedRef
              && references.references?.[`reference_${selectedRef}`]?.status === 'declared'
              && !references.references[`reference_${selectedRef}`]?.request?.sent_at
              && references.references?.[`reference_${selectedRef}`]?.can_send_reference_request === false && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                <p className="text-sm font-medium text-amber-800">Declaration incomplete</p>
                <ul className="mt-1 text-xs text-amber-700 list-disc list-inside">
                  {(references.references[`reference_${selectedRef}`].send_blockers || []).map((blocker, idx) => (
                    <li key={idx}>{blocker}</li>
                  ))}
                </ul>
              </div>
            )}

            {selectedRef && (() => {
              const declared = references.references?.[`reference_${selectedRef}`]?.declared || {};
              return (
                <div>
                  <Label className="text-sm font-medium">Delivery method</Label>
                  <div className="grid grid-cols-1 gap-2 mt-2 sm:grid-cols-3" data-testid="reference-delivery-options">
                    <Button
                      type="button"
                      variant={deliveryChannel === 'email' ? 'default' : 'outline'}
                      className="h-auto min-h-11 px-3 py-2"
                      disabled={!declared.email || sendingRequest === selectedRef}
                      onClick={() => {
                        setDeliveryChannel('email');
                        setManualWhatsAppUrl(null);
                      }}
                      data-testid="reference-delivery-email"
                    >
                      <Mail className="h-4 w-4 mr-1.5" />
                      Email
                    </Button>
                    <Button
                      type="button"
                      variant={deliveryChannel === 'whatsapp' ? 'default' : 'outline'}
                      className="h-auto min-h-11 px-3 py-2"
                      disabled={!declared.phone || sendingRequest === selectedRef}
                      onClick={() => {
                        setDeliveryChannel('whatsapp');
                        setManualWhatsAppUrl(null);
                      }}
                      data-testid="reference-delivery-whatsapp"
                    >
                      <MessageCircle className="h-4 w-4 mr-1.5" />
                      WhatsApp
                    </Button>
                    <Button
                      type="button"
                      variant={deliveryChannel === 'both' ? 'default' : 'outline'}
                      className="h-auto min-h-11 px-3 py-2"
                      disabled={!declared.email || !declared.phone || sendingRequest === selectedRef}
                      onClick={() => {
                        setDeliveryChannel('both');
                        setManualWhatsAppUrl(null);
                      }}
                      data-testid="reference-delivery-both"
                    >
                      <Send className="h-4 w-4 mr-1.5" />
                      Both
                    </Button>
                  </div>
                  {!declared.phone && (
                    <p className="mt-2 text-xs text-amber-700">
                      Add a referee phone number to enable WhatsApp.
                    </p>
                  )}
                </div>
              );
            })()}

            {(deliveryChannel === 'whatsapp' || deliveryChannel === 'both') && (
              <div
                className={`rounded-lg border p-3 ${
                  whatsappTemplateStatus?.ready
                    ? 'border-emerald-200 bg-emerald-50'
                    : 'border-amber-200 bg-amber-50'
                }`}
                data-testid="reference-whatsapp-template-status"
              >
                <div className="flex items-center justify-between gap-3">
                  <p className={`text-sm font-medium ${
                    whatsappTemplateStatus?.ready ? 'text-emerald-900' : 'text-amber-900'
                  }`}>
                    Meta first-contact status
                  </p>
                  {checkingWhatsAppTemplate ? (
                    <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
                  ) : whatsappTemplateStatus?.status ? (
                    <Badge variant="outline">
                      {whatsappTemplateStatus.status}
                    </Badge>
                  ) : null}
                </div>
                <p className={`mt-1 text-xs ${
                  whatsappTemplateStatus?.ready ? 'text-emerald-800' : 'text-amber-800'
                }`}>
                  {checkingWhatsAppTemplate
                    ? 'Checking Meta live — no message is being sent…'
                    : referenceWhatsAppTemplateCopy(whatsappTemplateStatus)}
                </p>
                {whatsappTemplateStatus?.category && (
                  <p className="mt-1 text-[11px] text-slate-600">
                    Category: {whatsappTemplateStatus.category}
                    {whatsappTemplateStatus.language ? ` · Language: ${whatsappTemplateStatus.language}` : ''}
                  </p>
                )}
              </div>
            )}

            <div>
              <label className="text-sm font-medium">Custom Message (Optional)</label>
              <Textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="Add a personalized message to the referee..."
                className="mt-1"
                rows={3}
              />
              <p className="mt-1 text-xs text-gray-500">
                Custom text is added to email. Meta first-contact WhatsApp uses the approved secure template.
              </p>
            </div>

            {manualWhatsAppUrl && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="reference-whatsapp-manual-fallback">
                <p className="text-sm font-medium text-amber-900">WhatsApp needs a manual send</p>
                <p className="mt-1 text-xs text-amber-800">
                  {referenceWhatsAppTemplateCopy(whatsappTemplateStatus)}
                  {' '}CareOps prepared a private reference link but did not send the WhatsApp message.
                  Open WhatsApp, review the recipient and press Send yourself. This is not recorded as delivered by CareOps.
                </p>
                <Button
                  type="button"
                  className="mt-3 w-full"
                  onClick={() => window.open(manualWhatsAppUrl, '_blank', 'noopener,noreferrer')}
                  data-testid="reference-open-whatsapp"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Open WhatsApp to send
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialogOpen(false)}>
              Cancel
            </Button>
            {!manualWhatsAppUrl && (
              <Button
                onClick={() => handleSendRequest(selectedRef, deliveryChannel)}
                disabled={
                  sendingRequest === selectedRef
                  || references.references?.[`reference_${selectedRef}`]?.can_send_reference_request === false
                  || (
                    (deliveryChannel === 'email' || deliveryChannel === 'both')
                    && !references.references?.[`reference_${selectedRef}`]?.declared?.email
                  )
                  || (
                    (deliveryChannel === 'whatsapp' || deliveryChannel === 'both')
                    && !references.references?.[`reference_${selectedRef}`]?.declared?.phone
                  )
                }
                data-testid="reference-send-selected-channel"
              >
                {sendingRequest === selectedRef ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : deliveryChannel === 'whatsapp' ? (
                  <MessageCircle className="h-4 w-4 mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                {deliveryChannel === 'whatsapp'
                  ? (whatsappTemplateStatus?.ready ? 'Send by WhatsApp' : 'Prepare WhatsApp message')
                  : deliveryChannel === 'both'
                    ? (whatsappTemplateStatus?.ready ? 'Send by Email + WhatsApp' : 'Send email + prepare WhatsApp')
                    : 'Send by Email'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Referee Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Add Referee Details</DialogTitle>
            <DialogDescription>
              Enter the referee's contact information. You can then send them a reference request.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={refereeForm.name}
                onChange={(e) => setRefereeForm({...refereeForm, name: e.target.value})}
                placeholder="e.g., Jane Smith"
                data-testid="referee-name-input"
              />
            </div>
            
            <div>
              <Label>Email Address *</Label>
              <Input
                type="email"
                value={refereeForm.email}
                onChange={(e) => setRefereeForm({...refereeForm, email: e.target.value})}
                placeholder="e.g., jane.smith@nhs.net"
                data-testid="referee-email-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use the email your referee will actually reply from — often their work or NHS address.
              </p>
            </div>
            
            <div>
              <Label>Phone Number</Label>
              <Input
                value={refereeForm.phone}
                onChange={(e) => setRefereeForm({...refereeForm, phone: e.target.value})}
                placeholder="e.g., 01234 567890"
              />
            </div>
            
            <div>
              <Label>Organisation *</Label>
              <Input
                value={refereeForm.organisation}
                onChange={(e) => setRefereeForm({...refereeForm, organisation: e.target.value})}
                placeholder="e.g., Previous Care Home Ltd"
              />
            </div>

            <div>
              <Label>Why this referee? (if not most recent employer)</Label>
              <Select
                value={refereeForm.slot_reason_type || undefined}
                onValueChange={(value) => setRefereeForm({ ...refereeForm, slot_reason_type: value })}
              >
                <SelectTrigger data-testid="referee-slot-reason-type">
                  <SelectValue placeholder="Select if needed..." />
                </SelectTrigger>
                <SelectContent>
                  {SLOT_REASON_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {refereeForm.slot_reason_type && (
              <div>
                <Label>
                  Brief note {refereeForm.slot_reason_type === 'other' ? '*' : '(optional)'}
                </Label>
                <Textarea
                  value={refereeForm.slot_reason_note}
                  onChange={(e) => setRefereeForm({ ...refereeForm, slot_reason_note: e.target.value })}
                  placeholder="Explain why this referee is appropriate for this reference slot."
                  rows={2}
                  data-testid="referee-slot-reason-note"
                />
              </div>
            )}
            
            <div>
              <Label>Job Title / Position</Label>
              <Input
                value={refereeForm.position}
                onChange={(e) => setRefereeForm({...refereeForm, position: e.target.value})}
                placeholder="e.g., Care Manager"
              />
            </div>
            
            <div>
              <Label>Relationship to Applicant</Label>
              <Input
                value={refereeForm.relationship}
                onChange={(e) => setRefereeForm({...refereeForm, relationship: e.target.value})}
                placeholder="e.g., Line Manager"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddReferee}
              disabled={
                isSubmitting
                || !refereeForm.name.trim()
                || !refereeForm.email.trim()
                || !refereeForm.organisation.trim()
                || (refereeForm.slot_reason_type === 'other' && (refereeForm.slot_reason_note || '').trim().length < 10)
              }
              data-testid="submit-referee-btn"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Add Referee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Review Response Dialog - Enhanced with categorized fields */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="sm:max-w-3xl bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              Full Reference Response - Referee {reviewRefNum}
            </DialogTitle>
            <DialogDescription>
              Complete response submitted by the referee for verification review.
            </DialogDescription>
          </DialogHeader>
          {reviewRefNum && references?.references?.[`reference_${reviewRefNum}`]?.response && (() => {
            const response = references.references[`reference_${reviewRefNum}`].response;
            const declared = references.references[`reference_${reviewRefNum}`].declared;
            
            // Categorize fields for better organization
            const refereeFields = ['referee_full_name', 'referee_organisation', 'referee_job_title', 'referee_work_email', 'referee_phone'];
            const relationshipFields = ['relationship_type', 'known_from_date', 'known_to_date', 'employment_dates_confirm', 'job_title_held', 'reason_for_leaving'];
            const performanceFields = ['performance_rating', 'reliability', 'professionalism', 'teamwork'];
            const suitabilityFields = ['safeguarding_concerns', 'disciplinary_record', 'would_re_employ', 're_employ_notes', 'care_vulnerable_suitable', 'care_suitability_notes'];
            const declarationFields = ['declaration_accurate', 'declaration_authority'];
            const skipFields = ['submitted_at', 'ip_address', 'user_agent'];
            
            const getRatingColor = (value) => {
              const v = String(value).toLowerCase();
              if (v.includes('excellent') || v === 'yes' || v.includes('suitable')) return 'text-green-700 bg-green-50';
              if (v.includes('good') || v.includes('reliable') || v.includes('professional')) return 'text-blue-700 bg-blue-50';
              if (v.includes('no concern') || v.includes('no issue')) return 'text-green-700 bg-green-50';
              if (v.includes('concern') || v === 'no') return 'text-red-700 bg-red-50';
              return 'text-gray-700 bg-gray-50';
            };
            
            const formatFieldName = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            
            const renderField = (key, value) => {
              if (skipFields.includes(key) || value === null || value === undefined) return null;
              const isRating = performanceFields.includes(key) || suitabilityFields.includes(key);
              const displayValue = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : (value || 'N/A');
              
              return (
                <div key={key} className={`rounded-lg p-3 ${isRating ? getRatingColor(displayValue) : 'bg-gray-50'}`}>
                  <p className="text-xs font-medium text-gray-500 mb-0.5">{formatFieldName(key)}</p>
                  <p className="text-sm font-medium">{displayValue}</p>
                </div>
              );
            };
            
            return (
              <div className="space-y-6 py-4">
                {/* Declared vs Returned Comparison */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="font-medium text-blue-800 mb-2 text-sm">Declared by Applicant</h4>
                    <p className="text-sm"><span className="text-blue-600">Name:</span> {declared?.name || 'N/A'}</p>
                    <p className="text-sm"><span className="text-blue-600">Organisation:</span> {declared?.organisation || 'N/A'}</p>
                    <p className="text-sm"><span className="text-blue-600">Email:</span> {declared?.email || 'N/A'}</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <h4 className="font-medium text-purple-800 mb-2 text-sm">Returned by Referee</h4>
                    <p className="text-sm"><span className="text-purple-600">Name:</span> {response.referee_full_name || 'N/A'}</p>
                    <p className="text-sm"><span className="text-purple-600">Organisation:</span> {response.referee_organisation || 'N/A'}</p>
                    <p className="text-sm"><span className="text-purple-600">Email:</span> {response.referee_work_email || 'N/A'}</p>
                  </div>
                </div>
                
                {/* Employment Period */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-gray-500" />
                    Employment Details
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {relationshipFields.map(key => response[key] !== undefined && renderField(key, response[key]))}
                  </div>
                </div>
                
                {/* Performance Ratings */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-gray-500" />
                    Performance Assessment
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {performanceFields.map(key => response[key] !== undefined && renderField(key, response[key]))}
                  </div>
                </div>
                
                {/* Suitability & Safeguarding */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Shield className="h-4 w-4 text-gray-500" />
                    Suitability & Safeguarding
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {suitabilityFields.map(key => response[key] !== undefined && renderField(key, response[key]))}
                  </div>
                </div>
                
                {/* Additional Comments - Full Width */}
                {response.additional_comments && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Additional Comments</h4>
                    <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 italic">
                      "{response.additional_comments}"
                    </div>
                  </div>
                )}
                
                {/* Declaration Confirmations */}
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">Referee Declarations</h4>
                  <div className="flex gap-4">
                    {response.declaration_accurate && (
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Information Accurate
                      </Badge>
                    )}
                    {response.declaration_authority && (
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Has Authority to Provide
                      </Badge>
                    )}
                  </div>
                </div>
                
                {/* Submission Timestamp */}
                {response.submitted_at && (
                  <p className="text-xs text-gray-500 text-center border-t pt-3">
                    Response submitted: {formatBackendDate(response.submitted_at)}
                  </p>
                )}
              </div>
            );
          })()}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
              Close
            </Button>

            {/* Download PDF button */}
            {reviewRefNum && (
              <Button
                variant="outline"
                onClick={() => handleDownloadReferencePdf(reviewRefNum)}
                disabled={downloadingPdf === reviewRefNum}
              >
                {downloadingPdf === reviewRefNum ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Download PDF
              </Button>
            )}

            {reviewRefNum && (() => {
              const currentRef = references?.references?.[`reference_${reviewRefNum}`];

              const isVerified =
                currentRef?.status === 'verified' ||
                currentRef?.verification?.status === 'verified' ||
                currentRef?.verification?.verified === true;

              if (isVerified) {
                return (
                  <div className="inline-flex items-center px-3 py-2 rounded-lg bg-green-50 text-green-700 border border-green-200 text-sm font-medium">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Already satisfactory
                  </div>
                );
              }

              return (
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    setReviewDialogOpen(false);
                    openVerifyDialog(reviewRefNum, 'verify');
                  }}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Mark Satisfactory
                </Button>
              );
            })()}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Verify/Reject Dialog */}
      <Dialog open={verifyDialogOpen} onOpenChange={setVerifyDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {verifyAction === 'verify' ? (
                <>
                  <Shield className="h-5 w-5 text-green-600" />
                  Mark Reference Satisfactory
                </>
              ) : (
                <>
                  <XCircle className="h-5 w-5 text-red-600" />
                  Mark Reference Unsatisfactory
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {verifyAction === 'verify' 
                ? 'Confirm that this reference meets workspace review standards. Response evidence and admin review will be recorded.'
                : 'Provide a reason for marking this reference unsatisfactory.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Reference Type (Reg 19) */}
            {verifyAction === 'verify' && (
              <div>
                <Label>Reference Type *</Label>
                <Select value={referenceType} onValueChange={setReferenceType}>
                  <SelectTrigger className="mt-1" data-testid="verify-reference-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employment">Employment</SelectItem>
                    <SelectItem value="character">Character</SelectItem>
                    <SelectItem value="academic">Academic</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  CQC Reg 19: Care roles require at least one employment reference or a recorded explanation.
                </p>
              </div>
            )}

            {/* Explanation — shown when admin picks non-employment OR when backend demands it */}
            {verifyAction === 'verify' && (explanationRequired || referenceType !== 'employment') && (
              <div>
                <Label className="text-amber-700">
                  Explanation for alternative reference *
                </Label>
                <Textarea
                  value={explanationReason}
                  onChange={(e) => setExplanationReason(e.target.value)}
                  placeholder="Why is an employment reference not available? (e.g. prior employer has ceased trading, gap in employment, first role, etc.)"
                  className="mt-1 border-amber-300"
                  rows={3}
                  data-testid="verify-reference-explanation"
                />
              </div>
            )}

            {/* Mismatch Handling */}
            {verifyAction === 'verify' && (
              <div>
                <Label>Organisation Mismatch Reason (if applicable)</Label>
                <Select value={mismatchReason || 'none'} onValueChange={(value) => setMismatchReason(value === 'none' ? '' : value)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select if referee not in employment history" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No mismatch</SelectItem>
                    <SelectItem value="earlier_employment">Referee is from earlier employment</SelectItem>
                    <SelectItem value="personal_reference">Referee is personal/professional reference</SelectItem>
                    <SelectItem value="changed_employers">Applicant changed employers since declaration</SelectItem>
                    <SelectItem value="other">Other (specify in notes)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label>{verifyAction === 'verify' ? 'Notes (Optional)' : 'Reason for Rejection *'}</Label>
              <Textarea
                value={verifyNotes}
                onChange={(e) => setVerifyNotes(e.target.value)}
                placeholder={verifyAction === 'verify' 
                  ? 'Any additional notes...'
                  : 'Explain why this reference is being rejected...'}
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerifyDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleVerifyReference}
              disabled={
                verifyLoading
                || (verifyAction === 'reject' && !verifyNotes)
                || (verifyAction === 'verify' && explanationRequired && !explanationReason.trim())
                || (verifyAction === 'verify' && referenceType !== 'employment' && !explanationReason.trim())
              }
              className={verifyAction === 'verify' ? 'bg-green-600 hover:bg-green-700' : ''}
              variant={verifyAction === 'reject' ? 'destructive' : 'default'}
            >
              {verifyLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : verifyAction === 'verify' ? (
                <CheckCircle className="h-4 w-4 mr-2" />
              ) : (
                <XCircle className="h-4 w-4 mr-2" />
              )}
              {verifyAction === 'verify' ? 'Mark Satisfactory' : 'Mark Unsatisfactory'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Mismatch Explanation Dialog */}
      <Dialog open={reviewExplanationOpen} onOpenChange={setReviewExplanationOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-amber-600" />
              Review Worker Explanation
            </DialogTitle>
            <DialogDescription>
              Review the worker's explanation for the reference mismatch. Accepting will clear the readiness block.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Decision *</Label>
              <Select value={reviewDecision} onValueChange={setReviewDecision}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a decision..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="accepted">Accept — explanation satisfactory</SelectItem>
                  <SelectItem value="needs_clarification">Needs Clarification</SelectItem>
                  <SelectItem value="rejected">Reject — insufficient justification</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Admin Notes (Optional)</Label>
              <Textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Any notes for the audit trail..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewExplanationOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleReviewExplanation}
              disabled={!reviewDecision || reviewingExplanation}
              className={reviewDecision === 'accepted' ? 'bg-green-600 hover:bg-green-700' : ''}
              variant={reviewDecision === 'rejected' ? 'destructive' : 'default'}
              data-testid="confirm-review-explanation-btn"
            >
              {reviewingExplanation ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Confirm Decision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

